import React, { useState, useCallback, useMemo } from 'react';
import {
    Box, Typography, IconButton, Button, TextField, Stack, Chip,
    Autocomplete, Avatar, Tooltip, Divider, alpha, useTheme
} from '@mui/material';
import {
    Add as AddIcon,
    Delete as DeleteIcon,
    ArrowUpward as ArrowUpIcon,
    ArrowDownward as ArrowDownIcon,
    Calculate as CalculateIcon,
    Place as PlaceIcon,
    Schedule as ScheduleIcon,
    FlightTakeoff as FlightIcon,
    DirectionsBus as BusIcon,
    MyLocation as StartIcon,
    Flag as EndIcon,
    Search as SearchIcon
} from '@mui/icons-material';
import LocationSearchField from '../LocationSearchField';
import { searchFlight } from '../../constants/common';
import moment from 'moment-timezone';

// Eligible activity types for stops
export const STOPS_ELIGIBLE_TYPES = [
    'TRANSPORT', 'NIGHTLINER', 'BUS', 'DRIVE', 'VAN', 'CAR',
    'SHUTTLE', 'TAXI', 'FERRY', 'TRAIN', 'TRANSFER', 'FLIGHT'
];

/**
 * Parse routeDetails from Activity — handles both v1 (flat array) and v2 (object with stops/legs).
 * Returns { version, stops, legs } always.
 */
export const parseRouteDetails = (routeDetailsStr) => {
    if (!routeDetailsStr) return { version: 2, stops: [], legs: [] };
    try {
        const parsed = JSON.parse(routeDetailsStr);
        if (parsed.version === 2) {
            return {
                version: 2,
                stops: parsed.stops || [],
                legs: parsed.legs || []
            };
        }
        // v1: flat array of drive/ferry segments — return as-is with empty stops
        return { version: 1, stops: [], legs: [], v1Segments: Array.isArray(parsed) ? parsed : [] };
    } catch (e) {
        return { version: 2, stops: [], legs: [] };
    }
};

/**
 * Serialize stops and legs into routeDetails v2 JSON string.
 */
export const serializeRouteDetails = (stops, legs) => {
    return JSON.stringify({ version: 2, stops, legs });
};

/**
 * Generate a unique stop ID.
 */
const genStopId = () => `stop-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;

/**
 * StopsEditor — A UI component for editing stops on a transport/flight activity.
 *
 * Props:
 *   activity — the current activity being edited (has type, locationId, endLocationId, etc.)
 *   stops — array of stop objects
 *   legs — array of leg objects
 *   onStopsChange — callback(stops, legs) when stops or legs change
 *   availableCrewMembers — array of crew members for crew-per-stop selection
 *   isFlightType — boolean, true for FLIGHT activities
 *   startLocation — start location object from activity
 *   endLocation — end location object from activity
 *   startTime — activity start time
 *   startTimeZone — activity start timezone
 */
const StopsEditor = ({
    activity,
    stops = [],
    legs = [],
    onStopsChange,
    availableCrewMembers = [],
    isFlightType = false,
    startLocation,
    endLocation,
    startTime,
    startTimeZone = 'Europe/Oslo',
    hideEndpoints = false
}) => {
    const theme = useTheme();
    const [calculating, setCalculating] = useState(false);

    // Helper: update stops and legs together
    const updateStopsAndLegs = useCallback((newStops, newLegs) => {
        onStopsChange(newStops, newLegs || legs);
    }, [onStopsChange, legs]);

    // Add a stop between start and end
    const addStop = useCallback(() => {
        const newStop = {
            id: genStopId(),
            type: isFlightType ? 'LAYOVER' : 'PICKUP',
            location: null,
            time: null,
            timeZone: startTimeZone,
            crewIds: [],
            notes: ''
        };

        // Insert before last stop (or at end if no stops yet)
        const newStops = [...stops, newStop];
        // Rebuild legs
        const newLegs = rebuildLegs(newStops);
        onStopsChange(newStops, newLegs);
    }, [stops, isFlightType, startTimeZone, onStopsChange]);

    // Remove a stop
    const removeStop = useCallback((idx) => {
        const newStops = stops.filter((_, i) => i !== idx);
        const newLegs = rebuildLegs(newStops);
        onStopsChange(newStops, newLegs);
    }, [stops, onStopsChange]);

    // Move stop up/down
    const moveStop = useCallback((idx, direction) => {
        const newStops = [...stops];
        const target = idx + direction;
        if (target < 0 || target >= newStops.length) return;
        [newStops[idx], newStops[target]] = [newStops[target], newStops[idx]];
        const newLegs = rebuildLegs(newStops);
        onStopsChange(newStops, newLegs);
    }, [stops, onStopsChange]);

    // Update a stop field
    const updateStop = useCallback((idx, field, value) => {
        const newStops = [...stops];
        newStops[idx] = { ...newStops[idx], [field]: value };
        onStopsChange(newStops, legs);
    }, [stops, legs, onStopsChange]);

    // Update a leg field
    const updateLeg = useCallback((idx, field, value) => {
        const newLegs = [...legs];
        newLegs[idx] = { ...newLegs[idx], [field]: value };
        onStopsChange(stops, newLegs);
    }, [stops, legs, onStopsChange]);

    // Rebuild legs array from stops
    const rebuildLegs = (stopsList) => {
        if (stopsList.length < 1) return [];
        const newLegs = [];
        // Legs connect: start → stop1, stop1 → stop2, ..., stopN → end
        // We generate legs between consecutive stops
        // Start and End are implicit (from activity location/endLocation)
        // So all stops are intermediate stops between start and end
        for (let i = 0; i <= stopsList.length; i++) {
            const fromId = i === 0 ? '__start__' : stopsList[i - 1].id;
            const toId = i === stopsList.length ? '__end__' : stopsList[i].id;
            // Preserve existing leg data if IDs match
            const existingLeg = legs.find(l => l.from === fromId && l.to === toId);
            newLegs.push(existingLeg || {
                from: fromId,
                to: toId,
                distance: null,
                duration: null,
                flightNumber: null,
                polyline: null
            });
        }
        return newLegs;
    };

    // Calculate route for a single leg using Google Directions
    const calculateLeg = useCallback(async (legIdx) => {
        if (!window.google) return;

        const leg = legs[legIdx];
        if (!leg) return;

        // Resolve from/to locations
        const fromLoc = leg.from === '__start__' ? startLocation : stops.find(s => s.id === leg.from)?.location;
        const toLoc = leg.to === '__end__' ? endLocation : stops.find(s => s.id === leg.to)?.location;

        if (!fromLoc?.latitude || !toLoc?.latitude) return;

        setCalculating(true);
        try {
            const directionsService = new window.google.maps.DirectionsService();
            const result = await directionsService.route({
                origin: { lat: fromLoc.latitude, lng: fromLoc.longitude },
                destination: { lat: toLoc.latitude, lng: toLoc.longitude },
                travelMode: window.google.maps.TravelMode.DRIVING
            });

            if (result.routes && result.routes.length > 0) {
                const route = result.routes[0];
                const legData = route.legs[0];
                const newLegs = [...legs];
                newLegs[legIdx] = {
                    ...newLegs[legIdx],
                    distance: (legData.distance.value / 1000).toFixed(1) + ' km',
                    duration: legData.duration.value, // seconds
                    polyline: route.overview_polyline?.points || null
                };
                onStopsChange(stops, newLegs);
            }
        } catch (err) {
            console.error('Route calculation failed for leg', legIdx, err);
        } finally {
            setCalculating(false);
        }
    }, [legs, stops, startLocation, endLocation, onStopsChange]);

    // Search flight info for a specific leg
    const searchFlightForLeg = useCallback(async (legIdx) => {
        const leg = legs[legIdx];
        if (!leg || !leg.flightNumber || !leg.flightNumber.trim()) return;
        setCalculating(true);
        try {
            const refTime = (legIdx > 0 && stops[legIdx - 1]?.time) ? stops[legIdx - 1].time : (startTime || moment().toISOString());
            const refTz = (legIdx > 0 && stops[legIdx - 1]?.timeZone) ? stops[legIdx - 1].timeZone : startTimeZone;
            const res = await searchFlight(refTime, refTz, leg.flightNumber.trim());
            if (!res) return;

            const newLegs = [...legs];
            newLegs[legIdx] = {
                ...newLegs[legIdx],
                flightNumber: res.flightNumber || leg.flightNumber.trim(),
                distance: res.distanceKm ? `${Number(res.distanceKm).toFixed(0)} km` : newLegs[legIdx].distance,
                duration: res.durationSeconds || newLegs[legIdx].duration
            };

            const newStops = [...stops];
            // If this leg ends at an intermediate stop (stop index === legIdx), set its location and arrivalTime
            if (legIdx < newStops.length) {
                newStops[legIdx] = {
                    ...newStops[legIdx],
                    location: res.endLocation || newStops[legIdx].location,
                    arrivalTime: res.endTime || newStops[legIdx].arrivalTime,
                    time: newStops[legIdx].time || res.endTime,
                    timeZone: res.endTimeZone || newStops[legIdx].timeZone
                };
            }
            // If this leg starts from an intermediate stop (stop index === legIdx - 1), update its departure time & location if missing
            if (legIdx > 0 && newStops[legIdx - 1]) {
                newStops[legIdx - 1] = {
                    ...newStops[legIdx - 1],
                    location: newStops[legIdx - 1].location || res.startLocation,
                    time: res.startTime || newStops[legIdx - 1].time,
                    timeZone: res.startTimeZone || newStops[legIdx - 1].timeZone
                };
            }
            onStopsChange(newStops, newLegs);
        } catch (err) {
            console.error('Flight search failed for leg', legIdx, err);
        } finally {
            setCalculating(false);
        }
    }, [legs, stops, startTime, startTimeZone, onStopsChange]);

    // Calculate all legs
    const calculateAllLegs = useCallback(async () => {
        if (!window.google || isFlightType) return;
        setCalculating(true);
        try {
            for (let i = 0; i < legs.length; i++) {
                await calculateLeg(i);
            }
        } finally {
            setCalculating(false);
        }
    }, [legs, calculateLeg, isFlightType]);

    // Total distance and duration
    const totals = useMemo(() => {
        let totalDuration = 0;
        let totalDistance = 0;
        legs.forEach(leg => {
            if (leg.duration) totalDuration += leg.duration;
            if (leg.distance) {
                const km = parseFloat(leg.distance);
                if (!isNaN(km)) totalDistance += km;
            }
        });
        return { totalDuration, totalDistance };
    }, [legs]);

    const formatDuration = (seconds) => {
        if (!seconds) return '--';
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        if (h > 0) return `${h}t ${m}m`;
        return `${m}m`;
    };

    // Get display name for a location
    const getLocName = (loc) => {
        if (!loc) return 'Ikke valgt';
        return loc.name || loc.formattedAddress || loc.city || 'Ukjent sted';
    };

    return (
        <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>
                    {isFlightType ? '✈️ Flight Legs' : '🚏 Stops'}
                </Typography>
                <Chip
                    label={`${stops.length} ${isFlightType ? 'layover(s)' : 'stopp'}`}
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            </Box>

            {/* Start point (from activity) */}
            {!hideEndpoints && (
                <StopRow
                    label="Start"
                    icon={<StartIcon fontSize="small" color="success" />}
                    locationName={getLocName(startLocation)}
                    time={startTime ? moment(startTime).tz(startTimeZone).format('HH:mm') : '--:--'}
                    isEndpoint
                    color={theme.palette.success.main}
                />
            )}

            {/* Intermediate stops */}
            {stops.map((stop, idx) => (
                <React.Fragment key={stop.id}>
                    {/* Leg line before this stop */}
                    <LegLine
                        leg={legs[idx]}
                        legIdx={idx}
                        isFlightType={isFlightType}
                        onCalculate={() => calculateLeg(idx)}
                        onSearchFlight={() => searchFlightForLeg(idx)}
                        onFlightNumberChange={(val) => updateLeg(idx, 'flightNumber', val)}
                        calculating={calculating}
                        formatDuration={formatDuration}
                    />

                    {/* Stop */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, my: 1 }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', pt: 0.5, minWidth: 32 }}>
                            <Box sx={{
                                width: 24, height: 24, borderRadius: '50%',
                                bgcolor: alpha(theme.palette.primary.main, 0.15),
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                border: `2px solid ${theme.palette.primary.main}`
                            }}>
                                <Typography variant="caption" sx={{ fontWeight: 800, fontSize: '0.65rem', color: 'primary.main' }}>
                                    {idx + 1}
                                </Typography>
                            </Box>
                        </Box>

                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            {/* Location */}
                            {stop.location ? (
                                <Chip
                                    icon={<PlaceIcon fontSize="small" />}
                                    label={getLocName(stop.location)}
                                    size="small"
                                    onDelete={() => updateStop(idx, 'location', null)}
                                    sx={{ mb: 0.5, maxWidth: '100%' }}
                                />
                            ) : (
                                <LocationSearchField
                                    label={isFlightType ? "Airport / City" : "Stopp-sted..."}
                                    onChange={(loc) => {
                                        if (loc) updateStop(idx, 'location', loc);
                                    }}
                                    size="small"
                                />
                            )}

                            {/* Time + Type row */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
                                {isFlightType && stop.arrivalTime && (
                                    <Chip
                                        label={`Arr ${moment(stop.arrivalTime).tz(stop.timeZone || startTimeZone).format('HH:mm')}`}
                                        size="small"
                                        variant="outlined"
                                        sx={{ height: 24, fontSize: '0.65rem', fontFamily: '"SF Mono", "Roboto Mono", monospace' }}
                                    />
                                )}
                                <TextField
                                    type="time"
                                    size="small"
                                    value={stop.time ? moment(stop.time).tz(stop.timeZone || startTimeZone).format('HH:mm') : ''}
                                    onChange={(e) => {
                                        if (!e.target.value) {
                                            updateStop(idx, 'time', null);
                                            return;
                                        }
                                        const [h, m] = e.target.value.split(':');
                                        const base = startTime ? moment(startTime).tz(stop.timeZone || startTimeZone) : moment().tz(stop.timeZone || startTimeZone);
                                        const newTime = base.clone().hour(parseInt(h)).minute(parseInt(m)).second(0);
                                        // If time is before start, assume next day
                                        if (startTime && newTime.isBefore(moment(startTime))) {
                                            newTime.add(1, 'day');
                                        }
                                        updateStop(idx, 'time', newTime.toISOString());
                                    }}
                                    sx={{ width: 110 }}
                                    InputProps={{
                                        startAdornment: <ScheduleIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary', fontSize: 16 }} />
                                    }}
                                />
                                <Chip
                                    label={stop.type || 'PICKUP'}
                                    size="small"
                                    variant="outlined"
                                    onClick={() => {
                                        const types = isFlightType
                                            ? ['LAYOVER', 'DEPARTURE', 'ARRIVAL']
                                            : ['PICKUP', 'DROPOFF'];
                                        const currentIdx = types.indexOf(stop.type || types[0]);
                                        updateStop(idx, 'type', types[(currentIdx + 1) % types.length]);
                                    }}
                                    color={stop.type === 'DROPOFF' ? 'error' : 'primary'}
                                    sx={{ cursor: 'pointer', fontSize: '0.65rem', height: 24 }}
                                />
                            </Box>

                            {/* Crew selector */}
                            {availableCrewMembers.length > 0 && (
                                <Autocomplete
                                    multiple
                                    size="small"
                                    options={availableCrewMembers}
                                    getOptionLabel={(opt) => {
                                        const u = opt.user;
                                        return u ? `${u.firstName || ''} ${u.lastName || ''}`.trim() : opt.name || '';
                                    }}
                                    value={availableCrewMembers.filter(cm => stop.crewIds?.includes(cm.user?.id))}
                                    onChange={(_, newVal) => {
                                        updateStop(idx, 'crewIds', newVal.map(cm => cm.user?.id).filter(Boolean));
                                    }}
                                    renderTags={(value, getTagProps) =>
                                        value.map((opt, i) => (
                                            <Chip
                                                {...getTagProps({ index: i })}
                                                key={opt.user?.id || i}
                                                size="small"
                                                avatar={<Avatar sx={{ width: 20, height: 20, fontSize: '0.6rem' }}>{(opt.user?.firstName || '?')[0]}</Avatar>}
                                                label={opt.user?.firstName || opt.name || '?'}
                                                sx={{ height: 22 }}
                                            />
                                        ))
                                    }
                                    renderInput={(params) => (
                                        <TextField {...params} placeholder="Crew på dette stoppet..." variant="outlined" sx={{ mt: 0.5 }} />
                                    )}
                                    sx={{ mt: 0.5 }}
                                />
                            )}
                        </Box>

                        {/* Controls */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, alignItems: 'center' }}>
                            <IconButton size="small" onClick={() => moveStop(idx, -1)} disabled={idx === 0}>
                                <ArrowUpIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => moveStop(idx, 1)} disabled={idx === stops.length - 1}>
                                <ArrowDownIcon fontSize="small" />
                            </IconButton>
                            <IconButton size="small" onClick={() => removeStop(idx)} color="error">
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Box>
                    </Box>
                </React.Fragment>
            ))}

            {/* Last leg line (to end) */}
            {stops.length > 0 && legs.length > stops.length && (
                <LegLine
                    leg={legs[stops.length]}
                    legIdx={stops.length}
                    isFlightType={isFlightType}
                    onCalculate={() => calculateLeg(stops.length)}
                    onSearchFlight={() => searchFlightForLeg(stops.length)}
                    onFlightNumberChange={(val) => updateLeg(stops.length, 'flightNumber', val)}
                    calculating={calculating}
                    formatDuration={formatDuration}
                />
            )}

            {/* End point (from activity) */}
            {!hideEndpoints && (
                <StopRow
                    label="End"
                    icon={<EndIcon fontSize="small" color="error" />}
                    locationName={getLocName(endLocation)}
                    time={activity?.endTimeTs ? moment(activity.endTimeTs).tz(activity.endTimeZone || startTimeZone).format('HH:mm') : '--:--'}
                    isEndpoint
                    color={theme.palette.error.main}
                />
            )}

            {/* Action buttons */}
            <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={addStop}
                >
                    {isFlightType ? 'Add Layover' : 'Legg til stopp'}
                </Button>

                {!isFlightType && stops.length > 0 && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CalculateIcon />}
                        onClick={calculateAllLegs}
                        disabled={calculating}
                        color="secondary"
                    >
                        {calculating ? 'Beregner...' : 'Kalkuler alle legs'}
                    </Button>
                )}
            </Box>

            {/* Totals */}
            {legs.some(l => l.distance || l.duration) && (
                <Box sx={{ mt: 1.5, p: 1, bgcolor: alpha(theme.palette.info.main, 0.08), borderRadius: 1, display: 'flex', gap: 2 }}>
                    <Typography variant="caption" fontWeight={600}>
                        Totalt: {totals.totalDistance > 0 ? `${totals.totalDistance.toFixed(1)} km` : '—'}
                    </Typography>
                    <Typography variant="caption" fontWeight={600}>
                        {totals.totalDuration > 0 ? formatDuration(totals.totalDuration) : '—'}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

// --- Sub-components ---

/**
 * StopRow — renders a start/end endpoint row.
 */
const StopRow = ({ label, icon, locationName, time, isEndpoint, color }) => {
    const theme = useTheme();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.75 }}>
            <Box sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                minWidth: 32
            }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, color, fontSize: '0.7rem' }}>
                    {label}
                </Typography>
                <Typography variant="caption" sx={{ display: 'block', color: 'text.primary', fontWeight: 500 }} noWrap>
                    {locationName}
                </Typography>
            </Box>
            <Typography variant="caption" sx={{
                fontWeight: 700, fontFamily: '"SF Mono", "Roboto Mono", monospace',
                color: 'text.secondary', fontSize: '0.75rem'
            }}>
                {time}
            </Typography>
        </Box>
    );
};

/**
 * LegLine — renders the connecting line between stops with distance/duration info.
 */
const LegLine = ({ leg, legIdx, isFlightType, onCalculate, onSearchFlight, onFlightNumberChange, calculating, formatDuration }) => {
    const theme = useTheme();

    if (!leg) return null;

    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            pl: 1.5, ml: 1.2,
            borderLeft: `2px dashed ${alpha(theme.palette.text.secondary, 0.3)}`,
            py: 0.5, minHeight: 32
        }}>
            {isFlightType ? (
                <FlightIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
            ) : (
                <BusIcon fontSize="small" sx={{ color: 'text.secondary', fontSize: 14 }} />
            )}

            {isFlightType && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <TextField
                        size="small"
                        placeholder="SK1234"
                        value={leg.flightNumber || ''}
                        onChange={(e) => onFlightNumberChange(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && leg.flightNumber && onSearchFlight) {
                                e.preventDefault();
                                onSearchFlight();
                            }
                        }}
                        sx={{ width: 100 }}
                        inputProps={{ style: { fontSize: '0.75rem', padding: '4px 8px' } }}
                    />
                    {onSearchFlight && (
                        <Tooltip title="Search flight leg">
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={onSearchFlight}
                                    disabled={calculating || !leg.flightNumber}
                                    color="primary"
                                    sx={{ p: 0.5 }}
                                >
                                    <SearchIcon fontSize="small" sx={{ fontSize: 16 }} />
                                </IconButton>
                            </span>
                        </Tooltip>
                    )}
                </Box>
            )}

            {leg.distance && (
                <Chip
                    label={leg.distance}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                />
            )}
            {leg.duration && (
                <Chip
                    label={formatDuration(leg.duration)}
                    size="small"
                    variant="outlined"
                    color="info"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                />
            )}

            {!isFlightType && !leg.distance && (
                <Tooltip title="Kalkuler denne leg">
                    <IconButton size="small" onClick={onCalculate} disabled={calculating}>
                        <CalculateIcon fontSize="small" sx={{ fontSize: 16 }} />
                    </IconButton>
                </Tooltip>
            )}
        </Box>
    );
};

export default StopsEditor;
